import { useTranslation } from 'next-i18next';
import Image from 'next/image';
import { SingleValue } from 'react-select';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { BsXLg } from 'react-icons/bs';
import dynamic from 'next/dynamic';
import classNames from 'classnames';
import { useRouter } from 'next/router';
import omit from 'lodash/omit';
import { PREFECTURES } from '../../../core/japaneseRegions';
import analytics from '../../../core/analytics/analytics';
import { COMMON_DATE_FORMAT, ContentType, SearchStatus } from '../../../core/constants';
import useIsAboveBreakpoint from '../../../core/hooks/useIsAboveBreakpoint';
import DatePicker from './DatePicker';
import {
  CheckInAndOutDates,
  RoomAndGuestCount,
} from '../../common/HotelAvailabilitySearch/useHotelSearchConditions';
import GuestCounter from './GuestCounter';
import Button from '../../common/Button';
import HotelSearchSelectOption from '../../../../types/HotelSearchSelectOption';
import SearchInput from './SearchInput';
import SearchOption from '../../common/SearchDropdown/SearchOption';
import useHotelSearchOptions from '../../../core/hotel/useHotelSearchOptions';
import convertCityPrefecture from '../../../core/helpers/convertCityPrefecture';

const Modal = dynamic(() => import('../../common/Modal/Modal'));

type Props = {
  dataTestId?: string;
  searchTerm?: SingleValue<HotelSearchSelectOption>;
  setSearchTerm: (newValue: SingleValue<HotelSearchSelectOption>) => void;
  checkInAndOutDates: CheckInAndOutDates;
  setCheckInAndOutDates: Dispatch<SetStateAction<CheckInAndOutDates>>;
  roomAndGuestCount: RoomAndGuestCount;
  setRoomAndGuestCount: Dispatch<SetStateAction<RoomAndGuestCount>>;
  guestCountString: string;
  shallow?: boolean;
};

const SimpleHotelSearchBar = ({
  dataTestId,
  searchTerm,
  setSearchTerm,
  checkInAndOutDates,
  setCheckInAndOutDates,
  roomAndGuestCount,
  setRoomAndGuestCount,
  guestCountString,
  shallow = false,
}: Props) => {
  const { t } = useTranslation('common');
  const isAboveMD = useIsAboveBreakpoint('MD');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchStatus, setSearchStatus] = useState<SearchStatus>(SearchStatus.PREFECTURE);
  const router = useRouter();
  const isLocaleJA = router.locale === 'ja';
  const searchTermLabel = isLocaleJA ? searchTerm?.label.ja : searchTerm?.label.en;

  const { searchInputValue, setSearchInputValue, options } = useHotelSearchOptions();

  const disabled = Boolean(
    !searchTerm?.value ||
      !checkInAndOutDates?.checkInDate ||
      !checkInAndOutDates?.checkOutDate ||
      !roomAndGuestCount?.roomCount ||
      !roomAndGuestCount?.adultCount
  );

  useEffect(() => {
    if (isAboveMD) {
      setIsModalOpen(false);
    }
  }, [isAboveMD, setIsModalOpen]);

  const events = {
    onClickOption: (value: SingleValue<HotelSearchSelectOption>) => {
      setSearchStatus(SearchStatus.DATE);
      setSearchTerm(value);

      const label = isLocaleJA ? value?.label.ja : value?.label.en;
      if (label) {
        setSearchInputValue(label);
      }
      analytics.google.basic.track.select(ContentType.SEARCH_DROPDOWN, value?.value || '');
    },
    onClickSearch: () => {
      if (!searchTerm) return;

      const commonQuery = {
        checkInDate: checkInAndOutDates.checkInDate?.format(COMMON_DATE_FORMAT),
        checkOutDate: checkInAndOutDates.checkOutDate?.format(COMMON_DATE_FORMAT),
        roomCount: roomAndGuestCount.roomCount.toString(),
        guestPerRoom: guestCountString,
      };

      if (searchTerm.type === 'property') {
        router.push(
          {
            pathname: `/h/${searchTerm.value}`,
            query: {
              ...commonQuery,
            },
          },
          undefined,
          {
            shallow,
          }
        );
      } else {
        const isCitySelected = searchTerm?.type === 'city';
        const isPrefectureSelected = PREFECTURES.find(
          (prefecture) => prefecture.path === searchTerm?.value
        );

        let type: 'region' | 'prefecture' | 'city';
        if (isCitySelected) {
          type = 'city';
        } else if (isPrefectureSelected) {
          type = 'prefecture';
        } else {
          type = 'region';
        }

        const isPrefectureChanged = router.query.q !== searchTerm.value;
        const searchValue = isCitySelected
          ? convertCityPrefecture(searchTerm.label.en, searchTerm.prefecture?.en ?? '').join('_')
          : searchTerm.value;

        router.push(
          {
            pathname: `/search/${type}/${searchValue}`,
            query: {
              ...omit(router.query, ['type', 'q', 'tab', 'hotel', 'map']),
              ...commonQuery,
              prefectureAreas: isPrefectureChanged ? '' : router.query.prefectureAreas, // reset areas only when changing prefecture
              latlng: isCitySelected ? searchTerm.value : '',
            },
          },
          undefined,
          {
            shallow,
          }
        );
      }
      analytics.google.basic.track.search(
        searchTerm.value,
        checkInAndOutDates?.checkInDate?.format(COMMON_DATE_FORMAT) as string,
        checkInAndOutDates?.checkOutDate?.format(COMMON_DATE_FORMAT) as string,
        roomAndGuestCount.roomCount,
        searchTerm.type,
        guestCountString
      );
    },
  };

  return (
    <div className="rounded-search-bar">
      <button
        className="flex h-16 w-full cursor-pointer items-center gap-2 rounded px-5"
        type="button"
        onClick={() => {
          // initialize search input value by searchTerm label
          setSearchInputValue(searchTermLabel || '');
          return setIsModalOpen(true);
        }}
        data-testid={dataTestId}
      >
        <Image src="/magnifying_glass.svg" alt="tabist-magazine-logo" width={17} height={17} />
        <p className="truncate">{searchTermLabel || t('search_header.search_hotel')}</p>
      </button>
      {isModalOpen && (
        <Modal
          title="Search"
          onClose={() => {
            setSearchStatus(SearchStatus.PREFECTURE);
            return setIsModalOpen(false);
          }}
          fullScreen
          header={
            <div className="fixed flex h-16 w-full items-center bg-orange-700 p-4 sm:p-8">
              <div className="flex w-full items-center justify-between">
                <div className="flex-1 truncate text-center text-medium font-bold text-white">
                  {t('button.search_hotels_short')}
                </div>
                <div className="flex flex-none">
                  <button
                    type="button"
                    onClick={() => {
                      setSearchStatus(SearchStatus.PREFECTURE);
                      return setIsModalOpen(false);
                    }}
                  >
                    <BsXLg className="text-white" size={18} />
                  </button>
                </div>
              </div>
            </div>
          }
        >
          <div className="flex h-full flex-col">
            <div
              className={classNames(
                'flex overflow-auto flex-col gap-4 p-4 pt-0 h-full bg-orange-700',
                {
                  'pb-6': searchStatus !== SearchStatus.DATE,
                  'pb-0': searchStatus === SearchStatus.DATE,
                }
              )}
            >
              {searchStatus === SearchStatus.PREFECTURE ? (
                <div className="rounded-3xl bg-white py-4">
                  <div className="flex h-7 items-center justify-center">
                    <p className="text-xl font-bold">{t('search_header.search_hotel')}</p>
                  </div>
                  <SearchInput
                    onChange={(e) => setSearchInputValue(e.target.value)}
                    value={searchInputValue}
                    onClear={() => setSearchInputValue('')}
                  />
                  {options?.length === 0 && (
                    <div className="flex h-14 items-center justify-center">
                      <p className="text-gray-500">{t('search_header.no_options')}</p>
                    </div>
                  )}
                  {options?.map((option) => (
                    <SearchOption
                      key={option.value}
                      option={option}
                      isSelected={searchTerm?.value === option.value}
                      isLocaleJA={isLocaleJA}
                      onClick={() => events.onClickOption(option)}
                    />
                  ))}
                </div>
              ) : (
                <button
                  className="flex h-14 w-full flex-none cursor-pointer items-center rounded-full bg-white px-5"
                  type="button"
                  onClick={() => setSearchStatus(SearchStatus.PREFECTURE)}
                  data-testid={dataTestId}
                >
                  <div className="flex-1">
                    <Image
                      src="/magnifying_glass.svg"
                      alt="tabist-magazine-logo"
                      width={17}
                      height={17}
                    />
                  </div>
                  <p className="flex-1 truncate">
                    {searchTermLabel || t('search_header.search_hotel')}
                  </p>
                  <div className="flex-1" />
                </button>
              )}
              <DatePicker
                checkInAndOutDates={checkInAndOutDates}
                setCheckInAndOutDates={setCheckInAndOutDates}
                searchStatus={searchStatus}
                setSearchStatus={setSearchStatus}
              />

              {searchStatus !== SearchStatus.DATE && (
                <GuestCounter
                  roomAndGuestCount={roomAndGuestCount}
                  setRoomAndGuestCount={setRoomAndGuestCount}
                  searchStatus={searchStatus}
                  setSearchStatus={setSearchStatus}
                />
              )}
            </div>

            {searchStatus !== SearchStatus.DATE && (
              <div className="footer flex w-full items-center justify-between border-t border-solid border-gray-200 bg-white px-8 py-4">
                <Button
                  color="red"
                  className="h-12 w-full"
                  disabled={disabled}
                  onClick={events.onClickSearch}
                >
                  {t('common:button.search_hotels_short')}
                </Button>
              </div>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
};

export default SimpleHotelSearchBar;
